import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import api from "@/lib/api";
import { ExternalLink, Search, ArrowLeft } from "lucide-react";

const GROUP_FALLBACK = {
  A: "Systems, Equipment, and Components",
  B: "Test, Inspection, and Production Equipment",
  C: "Materials",
  D: "Software",
  E: "Technology",
};

// Reason for Control code descriptions (15 CFR 738.2). Shown as tooltip text.
const REASON_TOOLTIP = {
  AT: "Anti-Terrorism", CB: "Chemical & Biological Weapons",
  CC: "Crime Control", CW: "Chemical Weapons Convention",
  EI: "Encryption Items", FC: "Firearms Convention",
  MT: "Missile Technology", NP: "Nuclear Nonproliferation",
  NS: "National Security", RS: "Regional Stability",
  SI: "Significant Items", SL: "Surreptitious Listening",
  SS: "Short Supply", UN: "United Nations Embargo",
};

export default function CCLCategory() {
  const { number } = useParams();
  const [meta, setMeta] = useState(null);
  const [entries, setEntries] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, e] = await Promise.all([
          api.get("/ccl/meta"),
          api.get("/ccl/entries", { params: { category: number } }),
        ]);
        if (!cancelled) {
          setMeta(m.data);
          setEntries(e.data.entries);
        }
      } catch {
        if (!cancelled) setEntries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [number]);

  // Deep-link scroll to hash after entries load (e.g. /ccl/5#5A002)
  useEffect(() => {
    if (!entries) return;
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [entries]);

  const parts = meta?.parts?.[number];  // e.g. { "1": "Telecommunications", "2": "\"Information Security\"" }
  const subsections = meta?.subsections || {};

  // Filtered entries (search) — one flat list; grouping happens below.
  const visibleEntries = useMemo(() => {
    if (!entries) return [];
    if (!q.trim()) return entries;
    const needle = q.trim().toLowerCase();
    return entries.filter(
      (e) =>
        e.eccn.toLowerCase().includes(needle) ||
        e.heading.toLowerCase().includes(needle) ||
        e.heading_full.toLowerCase().includes(needle)
    );
  }, [entries, q]);

  const categoryTitle = meta?.categories?.[number];
  const groupTitles = meta?.groups || GROUP_FALLBACK;
  const lastSyncedStr = meta?.last_synced
    ? new Date(meta.last_synced).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";

  // Build the tree of Parts (if any) → Groups → Subsections (if any) → ECCNs.
  const tree = useMemo(() => {
    const partKeys = parts ? Object.keys(parts) : [null];
    return partKeys.map((partKey) => {
      const partEntries = partKey
        ? visibleEntries.filter((e) => String(e.part) === partKey)
        : visibleEntries;
      const groups = Object.entries(groupTitles).map(([g, title]) => {
        const list = partEntries.filter((e) => e.group === g);
        if (list.length === 0) return null;
        const subKey = partKey ? `${number}|${partKey}|${g}` : null;
        const subs = subKey ? subsections[subKey] : null;
        if (subs && subs.length) {
          const subBuckets = subs.map((s) => ({
            roman: s.roman,
            title: s.title,
            list: list.filter((e) => e.subsection === s.roman),
          }));
          const uncategorized = list.filter((e) => !e.subsection);
          return { group: g, title, list, subs: subBuckets, uncategorized };
        }
        return { group: g, title, list };
      }).filter(Boolean);
      return { partKey, partTitle: partKey ? parts[partKey] : null, groups };
    });
  }, [visibleEntries, parts, groupTitles, subsections, number]);

  const totalInFilter = visibleEntries.length;

  if (meta && meta.categories && !meta.categories[number]) {
    return <Navigate to="/ccl" replace />;
  }

  return (
    <Layout>
      <SEO
        title={categoryTitle ? `CCL Category ${number} — ${stripQuotes(categoryTitle)}` : `CCL Category ${number}`}
        description={`Every ECCN in Commerce Control List Category ${number} — organized by Product Group. Search, filter, and jump to the current eCFR text.`}
        path={`/ccl/${number}`}
      />

      {/* Hero */}
      <section className="border-b border-hairline" data-testid={`ccl-cat-hero-${number}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10">
          <Link to="/ccl" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors" data-testid="ccl-back-link">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> All categories
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mt-6">
            <div className="lg:col-span-8">
              <Eyebrow>Category {number}</Eyebrow>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight mt-4">
                {categoryTitle || "Loading…"}
              </h1>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-1">
              <p className="section-num">{entries?.length ?? "—"}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">ECCNs in category</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-3">Last synced {lastSyncedStr}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Body: sidebar + main list */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="ccl-cat-body">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-40 lg:max-h-[calc(100vh-11rem)] lg:overflow-y-auto lg:pr-2" data-testid="ccl-cat-sidebar">
              <div className="relative mb-6">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" strokeWidth={2} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter this category"
                  className="w-full pl-10 pr-3 py-2 bg-ivory border border-hairline text-[13px] text-ink placeholder:text-ink-soft/70 focus:outline-none focus:border-brass"
                  data-testid="ccl-cat-filter"
                />
              </div>
              {q && (
                <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-4" data-testid="ccl-cat-filter-count">
                  {totalInFilter} match{totalInFilter === 1 ? "" : "es"}
                </p>
              )}
              {tree.map(({ partKey, partTitle, groups }) => (
                <div key={partKey || "flat"} className="mb-6">
                  {partTitle && (
                    <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold mb-3" data-testid={`ccl-cat-toc-part-${partKey}`}>
                      Part {partKey} — {stripQuotes(partTitle)}
                    </p>
                  )}
                  {!partTitle && (
                    <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold mb-3">Product Groups</p>
                  )}
                  <ul className="space-y-1">
                    {groups.map(({ group, title, list }) => (
                      <li key={group}>
                        <a
                          href={`#${anchorFor(partKey, group)}`}
                          className="flex items-baseline justify-between py-1.5 border-b border-hairline-soft transition-colors text-ink hover:text-brass"
                          data-testid={partKey ? `ccl-cat-toc-part-${partKey}-group-${group}` : `ccl-cat-toc-group-${group}`}
                        >
                          <span className="flex items-baseline gap-2">
                            <span className="font-mono text-[12px] font-semibold w-4">{group}</span>
                            <span className="text-[13px] leading-tight">{title}</span>
                          </span>
                          <span className="text-[11px] text-ink-soft">{list.length}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>

          {/* Main list */}
          <div className="lg:col-span-9">
            {entries === null && <p className="text-ink-soft">Loading ECCNs…</p>}
            {entries && entries.length === 0 && <p className="text-ink-soft">No ECCNs in this category.</p>}
            {entries && entries.length > 0 && q.trim() && totalInFilter === 0 && (
              <p className="text-ink-soft" data-testid="ccl-cat-no-match">No ECCNs in this category match &ldquo;{q}&rdquo;.</p>
            )}

            {entries && tree.map(({ partKey, partTitle, groups }) => (
              <div key={partKey || "flat"} className="mb-10">
                {partTitle && (
                  <div
                    id={`part-${partKey}`}
                    className="scroll-mt-40 border-t-2 border-brass-deep pt-6 mb-8"
                    data-testid={`ccl-cat-part-${partKey}`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold">Part {partKey}</p>
                    <h2 className="font-serif text-3xl text-ink mt-1">{stripQuotes(partTitle)}</h2>
                  </div>
                )}
                {groups.map(({ group, title, list, subs, uncategorized }) => (
                  <GroupBlock
                    key={group}
                    partKey={partKey}
                    group={group}
                    title={title}
                    list={list}
                    subs={subs}
                    uncategorized={uncategorized}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-ink text-ivory" data-testid="ccl-cat-disclaimer">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Note</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            This is a navigator, not the CCL itself. Verify every ECCN against the eCFR before relying on it. Nothing on this page is legal advice.
          </p>
        </div>
      </section>
    </Layout>
  );
}

function GroupBlock({ partKey, group, title, list, subs, uncategorized }) {
  const anchor = anchorFor(partKey, group);
  return (
    <div id={anchor} className="mb-10 scroll-mt-40" data-testid={partKey ? `ccl-cat-part-${partKey}-group-${group}` : `ccl-cat-group-${group}`}>
      <div className="border-b border-hairline pb-4 mb-6 flex items-baseline justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold">Product Group {group}</p>
          <h3 className="font-serif text-2xl text-ink mt-1">{title}</h3>
        </div>
        <span className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">{list.length}</span>
      </div>
      {subs
        ? (
          <>
            {subs.map((s) => (
              s.list.length > 0 && (
                <div key={s.roman} className="mb-8" data-testid={`ccl-cat-part-${partKey}-group-${group}-sub-${s.roman}`}>
                  <div className="mb-4 pl-4 border-l-2 border-brass/40">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-brass-deep font-semibold">Subsection {s.roman}</p>
                    <h4 className="font-serif text-lg text-ink mt-1 leading-snug">{s.title}</h4>
                  </div>
                  <EntryList list={s.list} />
                </div>
              )
            ))}
            {uncategorized && uncategorized.length > 0 && (
              <div className="mb-8" data-testid={`ccl-cat-part-${partKey}-group-${group}-sub-none`}>
                <p className="text-[10px] uppercase tracking-[0.24em] text-ink-soft font-semibold mb-4 pl-4 border-l-2 border-hairline">Other</p>
                <EntryList list={uncategorized} />
              </div>
            )}
          </>
        )
        : <EntryList list={list} />
      }
    </div>
  );
}

function EntryList({ list }) {
  return (
    <ul>
      {list.map((e) => (
        <li
          key={e.eccn}
          id={e.eccn}
          className="border-b border-hairline-soft py-4 grid grid-cols-12 gap-4 scroll-mt-40"
          data-testid={`ccl-entry-${e.eccn}`}
        >
          <div className="col-span-2 sm:col-span-1">
            <span className="font-mono text-[13px] font-semibold text-brass-deep">{e.eccn}</span>
          </div>
          <div className="col-span-10 sm:col-span-9">
            <p className="text-[15px] text-ink leading-snug">{e.heading}</p>
            {e.reasons?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`ccl-entry-${e.eccn}-reasons`}>
                {e.reasons.map((r) => (
                  <span
                    key={r}
                    title={REASON_TOOLTIP[r] || r}
                    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold font-mono tracking-wider uppercase border border-hairline bg-ivory-alt text-ink"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="col-span-12 sm:col-span-2 sm:text-right flex sm:flex-col sm:items-end gap-2 sm:gap-1">
            <Link
              to={`/ccl/e/${e.eccn}`}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-ink border-b border-brass hover:text-brass transition-colors"
              data-testid={`ccl-entry-${e.eccn}-read-full`}
            >
              Read full entry
            </Link>
            <a
              href={e.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-ink-soft border-b border-hairline hover:text-brass hover:border-brass transition-colors"
              data-testid={`ccl-entry-${e.eccn}-source`}
            >
              eCFR <ExternalLink className="w-3 h-3" strokeWidth={2} />
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
}

function anchorFor(partKey, group) {
  return partKey ? `part-${partKey}-group-${group}` : `group-${group}`;
}

function stripQuotes(s) {
  return String(s).replace(/["'"'"]/g, "");
}
